import { CRIMSON_500, GREEN_600, AMBER_500, VIOLET_400, MEDIUM_GRAY, BLUE } from "@styles/colors"
import i18next from 'i18next';

export const MINIMUM_TRANSACTION = 10_000
export const MINIMUM_BANK_REDEMPTION = 100_000
export const E_WALLET_REDEMPTION_LIMIT = 10_000_000
export const MINIMUM_PAYMENT_LINK_AMOUNT = 10_000
export const MINIMUM_BANK_TRANSFER_PAYMENT_AMOUNT = 10_000
export const TRANSACTION_CODE = 101 | 102 | 201 | 202 | 203 | 301 | 302 | 601 | 602 | 701 | 801

/**
 * Maps detailed activity fields to their respective display names, making use of the `t` function to fetch the localized string.
 * @example
 * const displayName = DETAILED_ACTIVITIES.asset_name; // this might return "Asset Name" depending on localization.
 */
/**
 * Gets the translated display name for a transaction field.
 * This function ensures translations are fetched on-demand when they're needed,
 * preventing issues with translations not being loaded at initialization time.
 */
export const getDetailedActivityLabel = (key: string): string => {
  const translationMap: Record<string, string> = {
    // Extra fields
    amount: 'transaction.extraFields.assetAmount',
    asset_name: 'transaction.extraFields.assetName',
    ex_rate: 'transaction.extraFields.exRate',
    deposit_tx_id: 'transaction.extraFields.depositTxId',
    bank: 'transaction.extraFields.bank',
    provider: 'transaction.extraFields.provider',
    account: 'transaction.extraFields.account',
    phone_number: 'transaction.extraFields.phoneNumber',
    account_name: 'transaction.extraFields.accountName',
    label: 'transaction.extraFields.label',
    transfer_date: 'transaction.extraFields.transferDate',
    transaction_reference: 'transaction.extraFields.transactionReference',
    approval_date: 'transaction.extraFields.approvalDate',
    decision_reason: 'transaction.extraFields.decisionReason',
    to: 'transaction.extraFields.to',
    from: 'transaction.extraFields.from',
    fee: 'transaction.extraFields.fee',
    total_amount: 'transaction.extraFields.totalAmount',
    address: 'transaction.extraFields.address',
    network: 'transaction.extraFields.network',
    merchant_fee: 'transaction.extraFields.merchantFee',
    convert_ui_amount: 'transaction.extraFields.convertUiAmount',
    network_fee: 'transaction.extraFields.networkFee',
    merchant_full_amount: 'transaction.extraFields.merchantFullAmount',
    redeem_tx_id: 'transaction.extraFields.redeemTxId',
    timestamp: 'transaction.generalFields.timestamp',
    top_up_amount: 'transaction.extraFields.topUpAmount',
    jidr_amount: 'transaction.extraFields.jidrAmount',
    virtual_account_number: 'transaction.extraFields.virtualAccountNumber',
    memo: 'transaction.extraFields.memo',

    // General fields
    uuid: 'transaction.generalFields.uuid',
    blockchain_tx_id: 'transaction.generalFields.blockchainTxId',
    ui_amount: 'transaction.generalFields.uiAmount',
    status: 'transaction.generalFields.status',

    // Payment link fields
    received_amount: 'transaction.extraFields.receivedAmount',
    payment_link_status: 'transaction.extraFields.paymentLinkStatus',
    payment_link: 'transaction.extraFields.paymentLink',
    merchant_name: 'transaction.extraFields.merchantName',
    merchant_email: 'transaction.extraFields.merchantEmail',
    expiry_time: 'transaction.extraFields.expiryTime',
    date: 'transaction.extraFields.date',

    // PPOB fields
    buyer_sku_code: 'transaction.extraFields.buyerSkuCode',
    customer_no: 'transaction.extraFields.customerNo',
    selling_price: 'transaction.extraFields.sellingPrice',
    sn: 'transaction.extraFields.sn',
    token: 'transaction.extraFields.token',
  };

  const translationKey = translationMap[key];
  return translationKey ? i18next.t(translationKey) : key;
};

// For backward compatibility with existing code that uses DETAILED_ACTIVITIES as an object
// Using Record<string, string> type to tell TypeScript this object can have any string key
export const DETAILED_ACTIVITIES: Record<string, string> = new Proxy({}, {
  get: (_target, prop) => {
    return getDetailedActivityLabel(prop.toString());
  }
})

// These functions return the appropriate translation for the current language
export const getTransactionName = (code: number): string => {
    return i18next.t(`components.transactions.name.${code}`);
};

export const getTransactionNameShort = (code: number): string => {
    return i18next.t(`components.transactions.nameShort.${code}`);
};

// Transaction codes for reference:
// 101: Top up from Bank
// 102: Top up from Crypto
// 201: Redeem to Bank
// 202: Redeem to Crypto
// 203: Redeem to E-Wallet
// 301: Payment
// 302: Payment Received
// 601: Transfer Received
// 602: Transfer Sent
// 701: Referral
// 801: Minting
// 901: Payment Point Online Bank


export const OUTGOING_TRANSACTION_CODE = Object.freeze([201, 202, 203, 301, 602, 901])

export const STATUS_COLOR = Object.freeze({
    CREATED: AMBER_500,
    INITIATED: AMBER_500,
    IN_PROGRESS: AMBER_500,
    PENDING: AMBER_500,
    MINTING: AMBER_500,
    FAILED: CRIMSON_500,
    UNDER_REVIEW: AMBER_500,
    PROCESSING: AMBER_500,
    PROCESSING_REFUND: AMBER_500,
    WAITING_FOR_CONFIRMATION: AMBER_500,
    WAITING_FOR_DISBURSEMENT: AMBER_500,
    PROCESSING_DISBURSEMENT: AMBER_500,
    COMPLETED: GREEN_600,
    REJECTED: CRIMSON_500,
    OPEN: VIOLET_400,
    EXPIRED: MEDIUM_GRAY,
    VOID: CRIMSON_500,
    ACTION_REQUIRED: BLUE,
    RETURNED: AMBER_500,
    WAITING_FOR_PAYMENT: AMBER_500,
    INVALID_DEPOSIT: CRIMSON_500
})

/**
 * Maps transaction status values to their localized translations.
 * Handles both uppercase and lowercase status strings from the backend.
 * @param status - The status string from the backend (e.g., 'COMPLETED', 'completed', 'PROCESSING_DISBURSEMENT')
 * @returns The localized status string
 * @example
 * const localizedStatus = getLocalizedStatus('COMPLETED'); // returns "Completed" in English
 * const localizedStatus = getLocalizedStatus('processing_refund'); // returns "Processing Refund" in English
 */
export const getLocalizedStatus = (status: string): string => {
  if (!status) return ''

  // Convert to lowercase and replace spaces with underscores for consistency
  const normalizedStatus = status.toLowerCase().replace(/ /g, '_')

  // Get the translation key
  const translationKey = `transaction.status.${normalizedStatus}`

  // Use i18next to get the translation
  return i18next.t(translationKey)
}

const generalFields = [
  { uuid: 'uuid' },
  { blockchain_tx_id: 'blockchain_tx_id' },
  { ui_amount: 'ui_amount' },
  { status: 'status' },
]

/**
 * Adjust Transaction detail when merging BankTransferPayment
 */
export const TRANSACTION_MAP = {
  // for transaction that uses transactions table
  old: {
    // top up from crypto
    102: [
      ...generalFields,
      { amount: 'extra.amount' },
      { asset_name: 'extra.asset_name' },
      { ex_rate: 'extra.ex_rate' },
      { deposit_tx_id: 'extra.deposit_tx_id' },
      { timestamp: 'timestamp' },
    ],
    // rtb
    201: [
      { uuid: 'uuid' },
      { ui_amount: 'ui_amount' },
      { status: 'status' },
      { bank: 'extra.bank' },
      { account: 'extra.account' },
      { account_name: 'extra.account_name' },
      { transfer_date: 'extra.transfer_date' },
      { approval_date: 'extra.approval_date' },
    ],
    // rtc
    202: [
      ...generalFields,
      { amount: 'extra.amount' },
      { asset_name: 'extra.asset_name' },
      { ex_rate: 'extra.ex_rate' },
      { address: 'extra.whitelist.address' },
      { network: 'extra.whitelist.network' },
      { redeem_tx_id: 'extra.redeem_tx_id' },
      { convert_ui_amount: 'extra.convert_ui_amount' },
      { network_fee: 'extra.network_fee' },
      { timestamp: 'timestamp' },
    ],
    // qr payment sent
    301: [
      ...generalFields,
      { to: 'extra.to' },
      { from: 'extra.from' },
      { timestamp: 'timestamp' },
    ],
    // qr payment received
    302: [
      ...generalFields,
      { to: 'extra.to' },
      { from: 'extra.from' },
      { merchant_fee: 'extra.merchant_fee' },
      { timestamp: 'timestamp' },
    ],
    // transfer received
    601: [
      { uuid: 'uuid' },
      { blockchain_tx_id: 'blockchain_tx_id' },
      { from: 'extra.transfer_from' },
      { ui_amount: 'ui_amount' },
      { status: 'status' },
      { timestamp: 'timestamp' },
    ],
    // transfer sent
    602: [
      { uuid: 'uuid' },
      { blockchain_tx_id: 'blockchain_tx_id' },
      { to: 'extra.transfer_to' },
      { ui_amount: 'ui_amount' },
      { status: 'status' },
      { timestamp: 'timestamp' },
    ],
  },
  // for transaction that uses activities table
  new: {
    // top up from bank
    101: [
      { uuid: 'uuid' },
      { top_up_amount: 'ui_amount' },
      { jidr_amount: 'ui_amount' },
      { status: 'status' },
      { bank: 'bank_code' },
      { virtual_account_number: 'account_number' },
      { timestamp: 'date_created' },
    ],
    // top up from crypto
    102: [
      ...generalFields,
      { amount: 'amount' },
      { asset_name: 'asset_name' },
      { ex_rate: 'ex_rate' },
      { deposit_tx_id: 'deposit_tx_id' },
      { timestamp: 'timestamp' },
    ],
    // rtb
    201: [
      { uuid: 'redeem_request.uuid' },
      { ui_amount: 'redeem_request.ui_amount' },
      { memo: 'redeem_request.memo' },
      { status: 'redeem_request.status' },
      { decision_reason: 'redeem_request.decision_reason' },
      { bank: 'redeem_request.bank.bank_name' },
      { account: 'redeem_request.bank.account_number' },
      { account_name: 'redeem_request.bank.account_holder' },
      { transfer_date: 'disbursement_requests.0.timestamp' },
      { approval_date: 'redeem_request.decision_date' },
      { timestamp: 'date_created' },
    ],
    // rte
    203: [
      { uuid: 'redeem_request.uuid' },
      { ui_amount: 'redeem_request.ui_amount' },
      { memo: 'redeem_request.memo' },
      { status: 'redeem_request.status' },
      { decision_reason: 'redeem_request.decision_reason' },
      { provider: 'redeem_request.ewallet.provider' },
      { label: 'redeem_request.ewallet.label' },
      { phone_number: 'redeem_request.ewallet.phone_number' },
      { transfer_date: 'disbursement_requests.0.timestamp' },
      { approval_date: 'redeem_request.decision_date' },
      { timestamp: 'date_created' },
    ],
    // bank transfer payment
    301: [
      { uuid: 'redeem_request.uuid' },
      { ui_amount: 'redeem_request.ui_amount' },
      { fee: 'redeem_request.ui_fee' },
      { total_amount: 'total_amount' },
      { memo: 'redeem_request.memo' },
      { status: 'redeem_request.status' },
      { decision_reason: 'redeem_request.decision_reason' },
      { bank: 'redeem_request.bank.bank_name' },
      { account: 'redeem_request.bank.account_number' },
      { account_name: 'redeem_request.bank.account_holder' },
      { timestamp: 'date_created' },
    ],
    // transfer received
    601: [
      { uuid: 'uuid' },
      { blockchain_tx_id: 'blockchain_tx_id' },
      { from: 'extra.transfer_from' },
      { ui_amount: 'ui_amount' },
      { status: 'status' },
      { timestamp: 'timestamp' },
    ],
    // transfer sent
    602: [
      { uuid: 'uuid' },
      { blockchain_tx_id: 'blockchain_tx_id' },
      { to: 'extra.transfer_to' },
      { ui_amount: 'ui_amount' },
      { status: 'status' },
      { timestamp: 'timestamp' },
    ],
    // referral reward
    701: [
      { uuid: 'uuid' },
      { ui_amount: 'referee_reward.referee_reward' },
      { ui_amount: 'referrer_reward.referrer_reward' },
      { timestamp: 'date_created' },
    ],
    // minting
    801: [
      { uuid: 'uuid' },
      { ui_amount: 'ui_amount' },
      { status: 'status' },
      { timestamp: 'date_created' },
    ],
    // ppob payment
    901: [
      { token: 'ppob_order.provider_metadata.token_number' },
      { uuid: 'uuid' },
      { ui_amount: 'ppob_order.selling_price_net' },
      { status: 'ppob_order.status' },
      { sn: 'ppob_order.sn' },
      { buyer_sku_code: 'ppob_order.buyer_sku_code' },
      { customer_no: 'ppob_order.customer_no' },
      { selling_price: 'ppob_order.selling_price_net' },
      { timestamp: 'date_created' },
    ],
    paymentlink: [
      { uuid: 'uuid' },
      { received_amount: 'received_amount' },
      { total_amount: 'expected_amount' },
      { payment_link_status: 'status' },
      { payment_link: 'link' },
      { merchant_name: 'merchant_name' },
      { merchant_email: 'merchant_email' },
      { buyer_email: 'buyer_email' },
      { expiry_time: 'expiry_time' },
      { date: 'created_at' },
    ],
  },
}
